/**
 * ChromeOverlays -- Global modal host for Bug Report + contextual Help.
 *
 * Bug-report and Help are global chrome actions, not module tools, so they
 * are no longer hosted in the right-hand Toolbox (module-tools-only, hidden
 * on the dashboard). They open here as a centred modal dialog, triggered by
 * the ActivityBar (and the website HeaderBar) via `chromeOverlayAtom`.
 *
 * Mounted ONCE per shell (EmbedShell + AppLayout) next to ActivityBar/Toolbox.
 * The dialog primitive is `@/shared/components/overlay/Dialog` (Radix-backed);
 * closing it resets the atom to `null`.
 *
 * MVC: this is a pure view. The only "logic" is deriving the active module id
 * from the current route so contextual Help still works — the same derivation
 * the shells use for the Toolbox.
 */

import { useLocation } from 'react-router-dom'
import { useAtom } from 'jotai'
import { chromeOverlayAtom } from '@/lib/stores/chromeOverlayAtoms'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/components/overlay/Dialog'
import { ReporterPanel } from './toolbox/ReporterPanel'
import { HelpPanel } from './toolbox/HelpPanel'

/**
 * Derive the active module id from the current pathname. Handles both the
 * embed shape (`/embed/main/<module>/...`) and the standalone-browser shape
 * (`/<module>/...`). Returns undefined on the dashboard / non-module routes
 * so Help falls back to its generic entry.
 */
function useActiveModuleFromPath(): string | undefined {
  const { pathname } = useLocation()
  const segments = pathname.split('/').filter(Boolean)
  if (segments[0] === 'embed' && segments[1] === 'main' && segments[2]) {
    return segments[2]
  }
  if (
    segments[0] &&
    !['embed', 'account', 'checkout', 'settings', 'modules'].includes(segments[0])
  ) {
    return segments[0]
  }
  return undefined
}

const OVERLAY_TITLES = {
  'bug-report': 'Feedback',
  help: 'Help',
} as const

export function ChromeOverlays() {
  const [overlay, setOverlay] = useAtom(chromeOverlayAtom)
  const currentModule = useActiveModuleFromPath()

  const isOpen = overlay !== null

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) setOverlay(null)
      }}
    >
      <DialogContent
        size="lg"
        fixed
        className="h-[min(640px,80vh)]"
        data-testid="chrome-overlay-content"
      >
        <DialogTitle className="sr-only">
          {overlay ? OVERLAY_TITLES[overlay] : ''}
        </DialogTitle>
        {overlay === 'bug-report' && <ReporterPanel />}
        {overlay === 'help' && <HelpPanel currentModule={currentModule} />}
      </DialogContent>
    </Dialog>
  )
}
